import { Close, CheckCircleOutline, EventBusy, EventAvailable } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  Paper,
  TableContainer,
  TextField,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import { useState, useContext } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { ModuleContext } from "../../context/ModuleContext";
import { UserModuleContext } from "../../context/UserModuleContext";

const WSU_RED = "#A60F2D";

// 9223372036854775807 (Int64.MaxValue) is AD's sentinel for "never expires" / "never set" —
const AD_FILETIME_EPOCH_DIFF_MS = 11644473600000;
const AD_NEVER_EXPIRES_THRESHOLD = 1e18;

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    "&:hover fieldset": { borderColor: WSU_RED },
    "&.Mui-focused fieldset": { borderColor: WSU_RED },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: WSU_RED },
};

export const ExpireADAccount = () => {
  const { setModule } = useContext(ModuleContext);
  const { userModule, setUserModule } = useContext(UserModuleContext);
  const { authState } = useOktaAuth();

  const [state, setState] = useState({
    error: null,
    waiting: false,
    comment: "",
    message: null,
  });

  const networkId = userModule.userData["Network ID"];
  const isExpired = userModule.ADData["Expire Date"] !== "never";
  const isFormValid = state.comment.trim();

  const handleError = async (res) => {
    const text = await res.text();
    const errorObj = JSON.parse(text);
    setState((prev) => ({
      ...prev,
      error: { message: errorObj?.error?.message || "Unknown server error" },
      waiting: false,
    }));
  };

  const formatAdFileTime = (value) => {
    const num = typeof value === "string" ? Number(value) : value;
    if (num === null || num === undefined || Number.isNaN(num) || num === 0) return "Not set";
    if (num > AD_NEVER_EXPIRES_THRESHOLD) return "Never expires";
    const ms = num / 10000 - AD_FILETIME_EPOCH_DIFF_MS;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

  const expireAccount = async () => {
    setState((prev) => ({ ...prev, waiting: true, error: null }));

    const now = new Date();
    const utcMillis = now.getTime() + now.getTimezoneOffset() * 60000;
    const expireDate = ((utcMillis * 1_000_000) / 100).toString();

    try {
      const res = await fetch(
          `${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/user/ad/attributes`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: authState.accessToken.accessToken,
            },
            body: JSON.stringify({
              networkId,
              dn: userModule.ADData.DN,
              comment: state.comment,
              attributes: { accountExpires: expireDate },
              callerId: authState.accessToken.claims.sub,
            }),
          }
      );

      if (!res.ok) return handleError(res);

      await res.json();
      setUserModule((prev) => ({
        ...prev,
        ADData: { ...prev.ADData, "Expire Date": now.toISOString() },
      }));
      setState({ comment: "", error: null, message: "Account has been expired in AD.", waiting: false });
    } catch (err) {
      setState((prev) => ({ ...prev, error: err, waiting: false }));
    }
  };

  const unexpireAccount = async () => {
    setState((prev) => ({ ...prev, waiting: true, error: null }));

    try {
      const res = await fetch(
          `${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/user/ad/attributes`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: authState.accessToken.accessToken,
            },
            body: JSON.stringify({
              networkId,
              dn: userModule.ADData.DN,
              comment: state.comment,
              attributes: { accountExpires: "9223372036854775807" },
              callerId: authState.accessToken.claims.sub,
            }),
          }
      );

      if (!res.ok) return handleError(res);

      await res.json();
      setUserModule((prev) => ({
        ...prev,
        ADData: { ...prev.ADData, "Expire Date": "never" },
      }));
      setState({ comment: "", error: null, message: "Account has been unexpired in AD.", waiting: false });
    } catch (err) {
      setState((prev) => ({ ...prev, error: err, waiting: false }));
    }
  };

  if (state.waiting) {
    return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
        </Box>
    );
  }

  if (state.message) {
    return (
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ backgroundColor: "#2e7d32", color: "white", px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
            <CheckCircleOutline fontSize="small" />
            <Typography variant="subtitle2" fontWeight={700}>
              Account Updated
            </Typography>
          </Box>

          <Box sx={{ px: 3, pt: 3, pb: 1 }}>
            <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
              Confirmation
            </Typography>
          </Box>

          <Box sx={{ px: 3, py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {state.message}
            </Typography>
          </Box>

          <Box sx={{ px: 3, pb: 4, display: "flex", justifyContent: "flex-end" }}>
            <Button
                variant="outlined"
                endIcon={<Close />}
                onClick={() => setModule((prev) => ({ ...prev, isVisible: false }))}
                sx={{ borderColor: "#ccc", color: "#666", "&:hover": { borderColor: "#aaa", backgroundColor: "#f9f9f9" } }}
            >
              Close
            </Button>
          </Box>
        </TableContainer>
    );
  }

  return (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>

        {/* Error Banner */}
        {state.error && (
            <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                Action failed
              </Typography>
              <Typography variant="body2">{state.error.message}</Typography>
            </Box>
        )}

        {/* ── Section: Account Expiry Status ── */}
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
            Account Expiry Status
          </Typography>
          <Typography variant="caption" sx={{ display: "block", color: "#777", mt: 0.25 }}>
            {networkId}
          </Typography>
        </Box>

        <Box sx={{ px: 3, py: 2 }}>
          <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 0.75,
                borderRadius: 2,
                backgroundColor: isExpired ? "#fce4ec" : "#e8f5e9",
                border: `1px solid ${isExpired ? WSU_RED : "#2e7d32"}`,
              }}
          >
            {isExpired
                ? <EventBusy fontSize="small" sx={{ color: WSU_RED }} />
                : <EventAvailable fontSize="small" sx={{ color: "#2e7d32" }} />
            }
            <Typography
                variant="body2"
                fontWeight={600}
                sx={{ color: isExpired ? WSU_RED : "#2e7d32" }}
            >
              {isExpired
                  ? `Expired: ${formatAdFileTime(userModule.ADData["Expire Date"])}`
                  : "Not Expired"}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mx: 3 }} />

        {/* ── Section: Justification ── */}
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
            Justification
          </Typography>
        </Box>

        <Box sx={{ px: 3, pb: 3 }}>
          <TextField
              required
              fullWidth
              size="small"
              multiline
              rows={3}
              label="Comment"
              value={state.comment}
              onChange={(e) => setState((prev) => ({ ...prev, comment: e.target.value }))}
              placeholder={`Provide a reason for ${isExpired ? "unexpiring" : "expiring"} this account`}
              sx={fieldSx}
          />
        </Box>

        {/* ── Actions ── */}
        <Box sx={{ px: 3, pb: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button
              variant="outlined"
              endIcon={<Close />}
              onClick={() => setModule((prev) => ({ ...prev, isVisible: false }))}
              sx={{
                borderColor: "#ccc",
                color: "#666",
                "&:hover": { borderColor: "#aaa", backgroundColor: "#f9f9f9" },
              }}
          >
            Cancel
          </Button>
          <Button
              variant="contained"
              endIcon={isExpired ? <EventAvailable /> : <EventBusy />}
              onClick={isExpired ? unexpireAccount : expireAccount}
              disabled={!isFormValid}
              sx={{
                backgroundColor: isExpired ? "#2e7d32" : WSU_RED,
                color: "white",
                px: 4,
                py: 1.25,
                fontWeight: 600,
                letterSpacing: 0.5,
                "&:hover": { backgroundColor: isExpired ? "#1b5e20" : "#8a0c25" },
                "&.Mui-disabled": { backgroundColor: "#ddd", color: "#aaa" },
              }}
          >
            {isExpired ? "Unexpire Account" : "Expire Account"}
          </Button>
        </Box>

      </TableContainer>
  );
};